import { useState, useEffect, useSyncExternalStore } from 'react';

const WORKSPACE_KEY = 'genofx:current_workspace_id';

/**
 * Custom hook that returns the current workspaceId and re-renders
 * whenever the workspace changes (via localStorage).
 * 
 * This solves the multi-tenancy caching problem across the app.
 */
export function useWorkspaceId(): string | null {
    // Use useSyncExternalStore for a truly reactive localStorage subscription
    const subscribe = (callback: () => void) => {
        // Listen for storage events (cross-tab)
        window.addEventListener('storage', callback);

        // Listen for custom event (same-tab changes)
        window.addEventListener('workspaceChanged', callback);

        return () => {
            window.removeEventListener('storage', callback);
            window.removeEventListener('workspaceChanged', callback);
        };
    };

    const getSnapshot = () => localStorage.getItem(WORKSPACE_KEY);

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Helper function to set the workspace ID and dispatch a custom event
 * so same-tab listeners are notified.
 */
export function setWorkspaceId(id: string): void {
    localStorage.setItem(WORKSPACE_KEY, id);
    window.dispatchEvent(new CustomEvent('workspaceChanged'));
}

/**
 * Helper to get the workspace ID (one-off, non-reactive).
 */
export function getWorkspaceId(): string | null {
    return localStorage.getItem(WORKSPACE_KEY);
}
